/**
 * Inhalt einer Tageszelle (Board „Vertrieb“): Preis und Mindestnächte mit Schloss, wo von Hand
 * gesetzt, der Standardpreis gedämpft; „gesperrt“ oder das Verbund-Zeichen; die Marke einer
 * Kanalregel oben rechts, ohne den Preis zu verdecken. Der Grund steht zusätzlich im zugänglichen
 * Namen der Zelle und in der Legende – Farbe ist nie der einzige Träger.
 */
import { Link2 } from 'lucide-react';
import type { AvailabilityDistributionChannelCloseout } from '@/shared/api';
import { DOUBLE_BOOKED_LABEL } from '@/features/properties/flat-family/DoubleBookedMark';
import { OtaLogo } from '@/features/properties/OtaLogo';
import { cn } from '@/shared/lib/cn';
import { MinimumStayWithTarget, PriceWithTarget } from '../automation/AutomationMarks';
import type { AutomationCell } from '../automation/automationModel';
import { dayAndMonth, rate, weekdayShort } from './calendarFormat';
import type { DayCellModel } from './calendarModel';

/** Derselbe Grund in Zelle und Legende; der Belegungskalender nennt ihn „Durch Verbund belegt“. */
export const FAMILY_CLOSED_LABEL = 'Durch Verbund geschlossen';

export function DayCellBody({
  cell,
  currency,
  automation = null,
}: {
  cell: DayCellModel;
  currency: string;
  automation?: AutomationCell | null;
}) {
  const { day } = cell;
  if (cell.kind === 'blocked') {
    return <span className="text-ink-soft text-[11px] font-bold">gesperrt</span>;
  }
  if (cell.kind === 'closedByFamily') {
    return <Link2 size={13} aria-hidden className="text-ink-faint" />;
  }
  return (
    <>
      <PriceWithTarget
        rate={day.rate}
        currency={currency}
        manual={day.rateSource === 'Manual'}
        prefill={day.rateSource === 'Prefill'}
        target={automation?.targetRate}
        className="text-[13px]"
      />
      <MinimumStayWithTarget
        value={day.minStay}
        manual={day.minimumStaySource === 'Manual'}
        target={automation?.targetMinimumStay}
        className="text-[10px]"
      />
    </>
  );
}

/** Eine noch nicht bestätigte Regel zeigt ihr Zeichen gedämpft, eine fehlgeschlagene rot gerahmt. */
export function CloseoutMark({ closeout }: { closeout: AvailabilityDistributionChannelCloseout }) {
  const channel = closeout.channels[0] === 'Airbnb' ? 'Airbnb' : 'BookingCom';
  return (
    <span
      aria-hidden
      className={cn(
        'bg-card absolute right-1 top-1 grid size-4 place-items-center rounded-[5px] border',
        closeout.syncStatus === 'Synced' && 'border-navy/30',
        closeout.syncStatus === 'Failed' && 'border-sealwax border-dashed',
        !['Synced', 'Failed'].includes(closeout.syncStatus) &&
          'border-line border-dashed opacity-70',
      )}
    >
      <OtaLogo channel={channel} size={10} />
    </span>
  );
}

export function stateLabel(cell: DayCellModel): string {
  if (cell.kind === 'booked') return 'Gebucht';
  if (cell.kind === 'doubleBooked') return DOUBLE_BOOKED_LABEL;
  // Der Server setzt `manualBlock` auch für einen Verkaufsstopp; der Stopp ist der genauere Grund.
  if (cell.kind === 'blocked') return cell.day.stopSell ? 'Verkaufsstopp' : 'Manuelle Sperre';
  if (cell.kind === 'closedByFamily') return FAMILY_CLOSED_LABEL;
  if (cell.kind === 'closedByRule') {
    return `${cell.closeout?.channels.join(' und ') ?? 'Kanal'} ${closeoutStatusText(cell.closeout?.syncStatus)}`;
  }
  return 'Lokal verfügbar';
}

/** Hinweis beim Darüberfahren: Tag, Preis, Mindestnächte und woher der Wert kommt. */
export function cellTitle(cell: DayCellModel, currency: string, extra = ''): string | undefined {
  if (cell.kind !== 'open' && cell.kind !== 'closedByRule') return undefined;
  const { day } = cell;
  const parts = [`${weekdayShort(day.date)} ${dayAndMonth(day.date)}`, rate(day.rate, currency)];
  if (day.minStay !== null) parts.push(`${day.minStay} Nächte mindestens`);
  if (day.rateSource === 'Prefill') parts.push('Standardpreis');
  if (day.rateSource === 'Manual' || day.minimumStaySource === 'Manual') {
    parts.push('von Hand gesetzt');
  }
  return parts.join(' · ') + extra;
}

/** Nur eine bestätigte Regel schließt wirklich; alles andere ist erst die Absicht dazu. */
function closeoutStatusText(
  status: AvailabilityDistributionChannelCloseout['syncStatus'] | undefined,
) {
  switch (status) {
    case 'Synced':
      return 'durch Regel geschlossen';
    case 'Failed':
      return 'soll per Regel schließen, Übertragung fehlgeschlagen';
    case 'NeedsReconcile':
    case 'Drifted':
      return 'soll per Regel schließen, Abgleich offen';
    default:
      return 'soll per Regel schließen, noch nicht bestätigt';
  }
}
